import type { Actions, Subjects } from '@/libs/ability'

export const APP_ROUTES = {
  home: '/',
  auth: {
    login: '/login',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
  },
  account: '/account',
  changePassword: '/change-password',
  selectOrganization: '/select-organization',
  accessDenied: '/access-denied',
  leaves: '/leaves',
  workLogs: '/work-logs',
  myWorkspace: '/my-workspace',
  teamOverview: '/team-overview',
  billing: '/billing',
  invoicing: {
    base: '/invoicing',
    new: '/invoicing/new',
    details: (id: string) => `/invoicing/${id}`,
  },
  economy: '/economy',
  pricing: '/pricing',
  roles: '/roles',
  campaigns: '/campaigns',
  integrations: '/integrations',
  loanCars: '/loan-cars',
  category: {
    base: '/categories',
    type: (categoryType: string) => `/categories/${categoryType}`,
    create: (categoryType: string) => `/categories/${categoryType}/new`,
    edit: (categoryType: string, id: string) => `/categories/${categoryType}/${id}/edit`,
    details: (categoryType: string, id: string) => `/categories/${categoryType}/${id}`,
  },
  customer: {
    base: '/customers',
    type: (customerEntityType: string) => `/customers/${customerEntityType}`,
    create: (customerEntityType: string) => `/customers/${customerEntityType}/new`,
    details: (id: string) => `/customers/${id}`,
    new: `/customers/new`,
    company: {
      base: '/customers/companies',
      create: `/customers/companies/new`,
      details: (id: string) => `/customers/companies/${id}`,
    },
  },
  communication: {
    base: '/communication',
    type: (templateType: string) => `/communication/${templateType}`,
    create: (templateType: string) => `/communication/${templateType}/new`,
    details: (templateType: string, id: string) => `/communication/${templateType}/${id}`,
  },
  dashboard: '/dashboard',
  booking: {
    base: '/booking',
    details: (id: string) => `/booking/${id}`,
    new: (customerID?: string, vehicleID?: string) =>
      `/booking/new${customerID ? `?customerID=${customerID}` : ''}${customerID ? '&' : vehicleID ? '?' : ''}${vehicleID ? `vehicleID=${vehicleID}` : ''}`,
  },
  employee: {
    base: '/employees',
    details: (id: string) => `/employees/${id}`,
    edit: (id: string) => `/employees/${id}/edit`,
    new: `/employees/new`,
  },
  rentCar: {
    base: '/rental-fleet',
    new: `/rental-fleet/new`,
    details: (id: string) => `/rental-fleet/${id}`,
  },

  settings: {
    base: '/settings',
    branchInfo: '/settings/branch-info',
    workingHours: '/settings/working-hours',
    permissions: '/settings/permissions',
    weeklyNotes: '/settings/weekly-notes',
    stores: {
      base: '/settings/stores',
      details: (id: string) => `/settings/stores/${id}`,
      new: `/settings/stores/new`,
    },
    qualifications: {
      base: '/settings/qualifications',
      details: (type: string, id: string) => `/settings/qualifications/${id}?type=${type}`,
      new: (type: string) => `/settings/qualifications/new?type=${type}`,
    },
    staff: {
      base: '/settings/staff',
      details: (id: string) => `/settings/staff/${id}`,
    },
    unionAgreements: {
      base: '/settings/union-agreements',
      details: (id: string) => `/settings/union-agreements/${id}`,
      new: '/settings/union-agreements/new',
    },
    category: {
      base: '/settings/categories',
      type: (categoryType: string) => `/settings/categories/${categoryType}`,
      create: (categoryType: string) => `/settings/categories/${categoryType}/new`,
      edit: (categoryType: string, id: string) => `/settings/categories/${categoryType}/${id}/edit`,
      details: (categoryType: string, id: string) => `/settings/categories/${categoryType}/${id}`,
    },
    packages: {
      base: '/settings/packages',
      new: '/settings/packages/new',
      details: (id: string) => `/settings/packages/${id}`,
    },
    absenceTypes: {
      base: '/settings/absence-types',
      new: '/settings/absence-types/new',
      details: (id: string) => `/settings/absence-types/${id}`,
    },
    service: {
      base: '/settings/services',
      new: `/settings/services/new`,
      edit: (id: string) => `/settings/services/${id}/edit`,
      details: (id: string) => `/settings/services/${id}`,
    },
    communication: {
      base: '/settings/communication',
      type: (templateType: string) => `/settings/communication/${templateType}`,
      create: (templateType: string) => `/settings/communication/${templateType}/new`,
      details: (templateType: string, id: string) =>
        `/settings/communication/${templateType}/${id}`,
    },
  },
  vehicle: {
    base: '/vehicles',
    details: (id: string) => `/vehicles/${id}`,
    new: `/vehicles/new`,
  },
  job: {
    base: '/jobs',
    details: (id: string) => `/jobs/${id}`,
    new: `/jobs/new`,
  },
  product: {
    base: '/products',
    details: (id: string) => `/products/${id}`,
    new: `/products/new`,
  },
}

type RoutePermission = {
  requiredAction: Actions
  requiredSubject: Subjects
}

const perm = (requiredAction: Actions, requiredSubject: Subjects): RoutePermission => ({
  requiredAction,
  requiredSubject,
})

export const ROUTE_PERMISSIONS = {
  customers: {
    read: perm('read', 'Customer'),
    create: perm('create', 'Customer'),
  },
  company: {
    read: perm('read', 'Company'),
    create: perm('create', 'Company'),
  },
  vehicles: {
    read: perm('read', 'Vehicle'),
    create: perm('create', 'Vehicle'),
  },
  services: {
    read: perm('read', 'Service'),
    create: perm('create', 'Service'),
  },
  jobs: {
    read: perm('read', 'Job'),
    create: perm('create', 'Job'),
  },
  booking: {
    read: perm('read', 'Job'),
    create: perm('create', 'Job'),
  },
  leaves: {
    read: perm('read', 'Leave'),
  },
  workLogs: {
    read: perm('read', 'WorkLog'),
  },
  billing: {
    read: perm('read', 'Billing'),
    create: perm('create', 'Billing'),
    update: perm('update', 'Billing'),
  },
  myWorkspace: {
    read: perm('read', 'WorkLog'),
  },
  teamOverview: {
    read: perm('read', 'WorkLog'),
  },
  products: {
    read: perm('read', 'Product'),
    create: perm('create', 'Product'),
  },
  rentalFleet: {
    read: perm('read', 'LoanCar'),
    create: perm('create', 'LoanCar'),
  },
  campaigns: {
    read: perm('read', 'Campaign'),
  },
  pricing: {
    read: perm('read', 'Pricing'),
  },
  categories: {
    read: perm('read', 'Category'),
    create: perm('create', 'Category'),
  },
  communication: {
    read: perm('read', 'Communication'),
    create: perm('create', 'Communication'),
  },
  settings: {
    read: perm('read', 'Settings'),
  },
  user: {
    read: perm('read', 'User'),
  },
}
